/**
 * Permission and Role Management Hooks
 *
 * These hooks provide functionality for checking permissions,
 * roles, and authorization-related operations.
 */
import { useAuthContext } from './core-hooks';
/**
 * Hook to check permissions
 */
export function useHasPermission() {
    const { hasPermission } = useAuthContext();
    return hasPermission;
}
/**
 * Hook to check roles
 */
export function useHasRole() {
    const { hasRole } = useAuthContext();
    return hasRole;
}
/**
 * Hook to check if user has a specific permission
 */
export function usePermission(permission) {
    const { hasPermission } = useAuthContext();
    return hasPermission(permission);
}
/**
 * Hook to check if user has a specific role
 */
export function useRole(role) {
    const { hasRole } = useAuthContext();
    return hasRole(role);
}
/**
 * Hook to check if user has any of the specified permissions
 */
export function useAnyPermission(permissions) {
    const { hasPermission } = useAuthContext();
    return permissions.some(permission => hasPermission(permission));
}
/**
 * Hook to check if user has all of the specified permissions
 */
export function useAllPermissions(permissions) {
    const { hasPermission } = useAuthContext();
    return permissions.every(permission => hasPermission(permission));
}
/**
 * Hook to check if user has any of the specified roles
 */
export function useAnyRole(roles) {
    const { hasRole } = useAuthContext();
    return roles.some(role => hasRole(role));
}
/**
 * Hook to check if user has all of the specified roles
 */
export function useAllRoles(roles) {
    const { hasRole } = useAuthContext();
    return roles.every(role => hasRole(role));
}
/**
 * Custom hook for permission checking (wrapper around auth context)
 * Provides a convenient interface for permission and role operations
 */
export function usePermissions() {
    const { hasPermission, hasRole, isAuthenticated } = useAuthContext();
    return {
        hasPermission,
        hasRole,
        isAuthenticated,
        // Convenience methods
        checkPermission: hasPermission,
        checkRole: hasRole,
        checkAnyPermission: (permissions) => permissions.some(permission => hasPermission(permission)),
        checkAllPermissions: (permissions) => permissions.every(permission => hasPermission(permission)),
        checkAnyRole: (roles) => roles.some(role => hasRole(role)),
        checkAllRoles: (roles) => roles.every(role => hasRole(role)),
    };
}
